// Receipt extraction orchestrator.
// Primary: Cloudflare Worker (multi-provider AI vision routing).
// Fallback: Tesseract.js OCR in browser + regex parser for SK receipts.
//
// Returns: { amount, date, vendor, ico, vatRate, items, confidence, source }
//   source: 'cloudflare' | 'gemini' | 'openrouter' | 'tesseract'

const WORKER_URL = process.env.REACT_APP_EXTRACT_WORKER_URL || '';
const WORKER_TOKEN = process.env.REACT_APP_EXTRACT_WORKER_TOKEN || '';

// --- Image compression (shared) -------------------------------------------

export async function compressImage(file, { maxEdge = 1600, quality = 0.82 } = {}) {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Nepodarilo sa prečítať obrázok.'));
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Nepodarilo sa načítať obrázok.'));
    i.src = src;
  });
}

// --- Worker call ----------------------------------------------------------

async function callWorker(imageDataUrl) {
  if (!WORKER_URL || !WORKER_TOKEN) {
    const err = new Error('worker_not_configured');
    err.code = 'no_worker';
    throw err;
  }
  const res = await fetch(`${WORKER_URL.replace(/\/$/, '')}/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${WORKER_TOKEN}`,
    },
    body: JSON.stringify({ image: imageDataUrl }),
  });

  if (res.status === 429) {
    const err = new Error('quota_exhausted');
    err.code = 'quota';
    throw err;
  }
  if (!res.ok) {
    const err = new Error(`worker_http_${res.status}`);
    err.code = 'http';
    throw err;
  }
  const data = await res.json();
  return { ...data, source: data.provider || 'worker' };
}

// --- Tesseract fallback (lazy-loaded) -------------------------------------

let _tesseractWorker = null;

async function getTesseract() {
  if (_tesseractWorker) return _tesseractWorker;
  const { createWorker } = await import('tesseract.js');
  _tesseractWorker = await createWorker(['slk', 'eng']);
  return _tesseractWorker;
}

async function tesseractExtract(imageDataUrl) {
  const worker = await getTesseract();
  const { data } = await worker.recognize(imageDataUrl);
  const text = data?.text || '';
  return { ...parseReceiptText(text), source: 'tesseract', _rawText: text };
}

// --- Regex parser for Slovak receipts -------------------------------------

function parseReceiptText(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const upper = text.toUpperCase();

  // Amount: look for SPOLU / CELKOM / TOTAL near a number with €
  const amount = findAmount(lines, upper);

  // Date: DD.MM.YYYY or DD/MM/YYYY or YYYY-MM-DD
  const date = findDate(text);

  // ICO: 8 consecutive digits, often after "IČO" / "ICO" / "IC"
  const ico = findIco(text);

  // VAT rate: "20%" / "DPH 20" / "10%" — pick highest mentioned rate
  const vatRate = findVatRate(text);

  // Vendor: first non-empty line that's not a number/header (heuristic)
  const vendor = findVendor(lines);

  const filled = [amount, date, ico, vendor].filter(Boolean).length;
  const confidence = Math.min(0.85, 0.3 + filled * 0.15);

  return {
    amount,
    date,
    vendor: vendor || '',
    ico: ico || '',
    vatRate,
    items: [],
    confidence: amount || vendor ? confidence : 0,
  };
}

function findAmount(lines, upper) {
  // Look for explicit total markers
  const markers = ['SPOLU', 'CELKOM', 'TOTAL', 'K ÚHRADE', 'K UHRADE', 'SUMA'];
  for (let i = 0; i < lines.length; i++) {
    const u = lines[i].toUpperCase();
    if (markers.some(m => u.includes(m))) {
      const num = extractNumber(lines[i]) || extractNumber(lines[i + 1] || '');
      if (num) return num;
    }
  }
  // Fallback: largest number with € symbol
  const matches = [...upper.matchAll(/(\d{1,5}[.,]\d{2})\s*(?:€|EUR)/g)];
  if (matches.length) {
    const nums = matches.map(m => parseFloat(m[1].replace(',', '.'))).filter(n => !isNaN(n));
    if (nums.length) return Math.max(...nums);
  }
  return null;
}

function extractNumber(s) {
  const m = s.match(/(\d{1,5}[.,]\d{2})/);
  if (!m) return null;
  const n = parseFloat(m[1].replace(',', '.'));
  return isNaN(n) ? null : n;
}

function findDate(text) {
  // DD.MM.YYYY or DD/MM/YYYY
  const m1 = text.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (m1) {
    const dd = m1[1].padStart(2, '0');
    const mm = m1[2].padStart(2, '0');
    return `${m1[3]}-${mm}-${dd}`;
  }
  // YYYY-MM-DD
  const m2 = text.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return null;
}

function findIco(text) {
  const m = text.match(/I[ČC]O\s*[:.]?\s*(\d{8})/i);
  if (m) return m[1];
  // Standalone 8 digits surrounded by whitespace/punctuation
  const m2 = text.match(/(?:^|\s)(\d{8})(?:\s|$)/);
  return m2 ? m2[1] : null;
}

function findVatRate(text) {
  const rates = [...text.matchAll(/\b(20|23|10|5|0)\s*%/g)].map(m => parseInt(m[1], 10));
  if (!rates.length) return null;
  return Math.max(...rates);
}

function findVendor(lines) {
  for (const line of lines.slice(0, 5)) {
    if (line.length < 3 || line.length > 40) continue;
    if (/^\d/.test(line)) continue;
    if (/(faktúra|doklad|pokladn|receipt|bill)/i.test(line)) continue;
    return line;
  }
  return null;
}

// --- Public API -----------------------------------------------------------

export async function extractFromImage(file, { onProgress } = {}) {
  const compressed = await compressImage(file);

  // Try Worker first
  if (WORKER_URL && WORKER_TOKEN) {
    try {
      onProgress?.('Posielam doklad na AI...');
      const data = await callWorker(compressed);
      return data;
    } catch (e) {
      if (e.code === 'no_worker' || e.code === 'quota') {
        // Fall through to Tesseract
      } else {
        // Network/other — also fall through, don't fail user
        console.warn('Worker call failed, falling back to Tesseract:', e.message);
      }
    }
  }

  // Tesseract fallback
  onProgress?.('Čítam doklad lokálne (OCR)...');
  return await tesseractExtract(compressed);
}

export async function disposeTesseract() {
  if (_tesseractWorker) {
    try { await _tesseractWorker.terminate(); } catch {}
    _tesseractWorker = null;
  }
}
